import React, { useState, useEffect, useRef } from "react";
import { FedaCheckoutButton } from "fedapay-reactjs";

const FedaPayButton = ({ onSuccess, product, quantity, subscriptionType, onBeforePayment, mensuration, phoneNumber, isPreorder = false, preorderAmount = 0, buttonText = null, disabled = false, customerData }) => {
  const [userInfo, setUserInfo] = useState(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const buttonRef = useRef(null)



  // Clé sandbox (à remplacer par votre clé réelle dans les variables d'environnement)
  // Ajoutez un fallback plus clair pour la clé publique
  const PUBLIC_KEY = process.env.REACT_APP_FEDAPAY_PUBLIC_KEY;
  if (!PUBLIC_KEY) {
    throw new Error("Clé publique FedaPay non configurée. Vérifiez REACT_APP_FEDAPAY_PUBLIC_KEY dans .env");
  }

  const handleWrapperClick = (e) => {
    if (disabled) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    return true;
  };

  // Bloque les événements sur le bouton
  useEffect(() => {
    if (buttonRef.current) {
      const btn = buttonRef.current.querySelector('button');
      if (btn) {
        if (disabled) {
          btn.style.pointerEvents = 'none';
          btn.style.opacity = '0.6';
          btn.style.cursor = 'not-allowed';
        } else {
          btn.style.pointerEvents = 'auto';
          btn.style.opacity = '1';
          btn.style.cursor = 'pointer';
        }
      }
    }
  }, [disabled]);

  useEffect(() => {
    if (isScriptLoaded && window.FedaPay) {
      const handleCustomerData = (event) => {
        if (event.detail && event.detail.customer) {
          // Juste pour le debug, vous pouvez supprimer ce log après
          console.log("Données client reçues:", event.detail.customer);
        }
      };

      window.addEventListener('fedapay.customer.data', handleCustomerData);

      return () => {
        window.removeEventListener('fedapay.customer.data', handleCustomerData);
      };
    }
  }, [isScriptLoaded]);

  const calculateAmount = () => {
    if (isPreorder) {
      return preorderAmount;
    }
    if (subscriptionType === 'mensuel') {
      return product.prix_mensuel;
    } else if (subscriptionType === 'annuel') {
      return product.prix_annuel;
    }
    // Par défaut (paiement unique)
    return product.prix * quantity;
  };

  const total = calculateAmount();

  const getUnitPrice = () => {
    if (subscriptionType === 'mensuel') {
      return product.prix_mensuel;
    } else if (subscriptionType === 'annuel') {
      return product.prix_annuel;
    }
    return product.prix;
  };


  useEffect(() => {
    // Chargement dynamique du script FedaPay
    const initializeFedaPay = async () => {
      try {
        // 1. Chargement du script
        if (!window.FedaPay) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://cdn.fedapay.com/checkout.js';
            script.async = true;
            script.onload = resolve;
            script.onerror = reject;
            document.body.appendChild(script);
          });
        }

        // 2. Vérification de l'initialisation
        if (!window.FedaPay) {
          throw new Error("FedaPay n'a pas été chargé correctement");
        }

        setIsScriptLoaded(true);
      } catch (error) {
        console.error("Erreur d'initialisation FedaPay:", error);
        setApiError(error.message);
      }
    };

    // Récupération des infos utilisateur
    const fetchUser = async () => {
      const user_id = localStorage.getItem("IDDDD");

      // Si aucun utilisateur, on continue sans fetch
      if (!user_id) {
        setIsLoading(false);
        return;
      }

      try {
        const res = await fetch(`${process.env.REACT_APP_HOTE}/recup-info-profil/${user_id}`);
        if (!res.ok) throw new Error("Erreur API");

        setUserInfo(await res.json());
      } catch (err) {
        console.error("Fetch user error:", err);
        setApiError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    initializeFedaPay();
    fetchUser();
  }, []);

  const handlePaymentSuccess = async (response) => {

    if (onBeforePayment && !onBeforePayment()) {
      return; // Arrête le processus si la vérification échoue
    }
    console.log("Données de mensuration reçues:", mensuration); // Ajoutez ce log

    if (response.reason === window.FedaPay.DIALOG_DISMISSED) {
      return; // Paiement annulé silencieusement
    }

    // Envoi des données au backend Django
    try {
      const user_id = localStorage.getItem("IDDDD");
      const transactionData = {
        transaction_id: response.transaction.id,
        amount: total,
        product_id: product.id,
        quantity: quantity,
        unit_price: getUnitPrice(),
        subscription_type: subscriptionType || 'none',
        status: 'completed',
        phone_number: phoneNumber, // Ajout du numéro de téléphone
        mensuration: mensuration,
        is_preorder: isPreorder,
        customer_firstname: response.transaction.customer?.firstname || customerData.firstname,
        customer_lastname: response.transaction.customer?.lastname || customerData.lastname,
        customer_email: response.transaction.customer?.email || customerData.email
      };

      console.log("Données client envoyées:", {
        firstname: response.transaction.customer?.firstname || customerData.firstname,
        lastname: response.transaction.customer?.lastname || customerData.lastname,
        email: response.transaction.customer?.email || customerData.email
      });

      console.log("Final transaction data:", transactionData); // Ajoutez ce log


      const endpoint = user_id === 'null'
        ? `${process.env.REACT_APP_HOTE}/save-transaction/`
        : `${process.env.REACT_APP_HOTE}/save-transaction/${user_id}/`;

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(transactionData)
      });

      // Vérifiez d'abord si la réponse est JSON
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Réponse inattendue: ${text.substring(0, 100)}...`);
      }

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Erreur serveur");
      }

      const data = await res.json();
      console.log("Réponse du backend:", data);

      localStorage.setItem('paymentSuccess', 'true');
      localStorage.setItem(
        "paymentData",
        JSON.stringify({
          transaction_id: data.transaction_id,
          amount: data.total,
          new_datefin: data.dates_abonnement?.fin,
        })
      );

      console.log('Données client envoyées:', {
        fedapay: response.transaction.customer,
        form: customerData,
        fullname: `${response.transaction.customer?.firstname || customerData.firstname} ${response.transaction.customer?.lastname || customerData.lastname}`
      });

      onSuccess({
        ...data,
        fedapay_response: response.transaction,
        customer_fullname: `${response.transaction.customer?.firstname || customerData.firstname} ${response.transaction.customer?.lastname || customerData.lastname}`
      });

    } catch (err) {
      console.error("Erreur transaction:", err);
      setApiError(err.message.includes('Unexpected token')
        ? "Erreur de communication avec le serveur"
        : err.message);
    }
  };

  if (!PUBLIC_KEY) {
    return (
      <div className="alert alert-danger">
        <h5>Configuration FedaPay manquante</h5>
        <p>Veuillez configurer REACT_APP_FEDAPAY_PUBLIC_KEY</p>
      </div>
    );
  }

  if (apiError) {
    return (
      <div className="alert alert-danger">
        {apiError}
        <button onClick={() => window.location.reload()} className="btn btn-sm btn-link">
          Réessayer
        </button>
      </div>
    );
  }



  if (isLoading || !isScriptLoaded) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="alert alert-warning">
        Aucun produit sélectionné
      </div>
    );
  }

  const paymentOptions = {
    public_key: PUBLIC_KEY,
    transaction: {
      amount: total,
      description: isPreorder
        ? `Précommande: ${product.nom} (x${quantity})  - Acompte 50% de taille ${mensuration.valeur}`
        : subscriptionType
          ? `Abonnement ${subscriptionType}: ${product.nom}`
          : `Achat: ${product.nom} (x${quantity})`,
      metadata: {
        product_id: product.id,
        unit_price: getUnitPrice(),
        quantity: quantity,
        subscription_type: subscriptionType || 'none',
        user_id: localStorage.getItem("IDDDD"),
        is_preorder: isPreorder
      }
    },
    customer: {
      email: customerData.email || "",
      lastname: customerData.lastname || "",
      firstname: customerData.firstname || ""
    },
    currency: { iso: "XOF" },
    button: {
      class: isPreorder
        ? `btn ${disabled ? 'btn-secondary' : 'btn-preorder'} w-100 py-2`
        : `btn ${disabled ? 'btn-secondary' : 'btn-wine'} w-100 py-2`,
      text: buttonText || (
        subscriptionType
          ? `S'abonner (${total.toLocaleString()} FCFA/${subscriptionType === 'mensuel' ? 'mois' : 'an'})`
          : `Payer ${total.toLocaleString()} FCFA`),
      disabled: disabled // Désactive le bouton FedaPay si nécessaire
    },
    onComplete: handlePaymentSuccess
  };

  console.log("Clé FedaPay utilisée:", process.env.REACT_APP_FEDAPAY_PUBLIC_KEY);
  console.log("Environnement:", process.env.REACT_APP_FEDAPAY_ENV || "sandbox");

  return (
    <div
      ref={buttonRef}
      onClick={handleWrapperClick}
      style={disabled ? { position: 'relative' } : {}}
    >
      {disabled && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10,
          backgroundColor: 'rgba(255,255,255,0.5)'
        }}></div>
      )}

      <FedaCheckoutButton
        options={paymentOptions}
        key={`feda-${total}-${disabled}`}
      />
    </div>
  );
};

export default FedaPayButton;